<template>
  <v-container fluid class="py-4">
    <v-row justify="center">
      <v-col cols="12" md="8" lg="8">
        <v-card outlined class="rounded-lg pa-6 elevation-1">
          <!-- Card Title Header -->
          <div class="d-flex align-center mb-4 pb-2 border-bottom">
            <v-icon color="cyan" class="mr-2">mdi-lock-reset</v-icon>
            <span class="text-h6 font-weight-bold grey--text text--darken-3"
              >Update Password</span
            >
          </div>

          <v-form
            ref="passwordForm"
            v-model.trim="formValid"
            lazy-validation
            v-on:submit.stop.prevent="onSubmit"
          >
            <input type="hidden" v-model.trim="formData.engineer" />

            <!-- New Password Field -->
            <div
              class="subtitle-2 font-weight-bold grey--text text--darken-2 mb-1"
            >
              New Password
            </div>
            <v-text-field
              :append-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
              :type="show2 ? 'text' : 'password'"
              v-on:click:append="show2 = !show2"
              dense
              outlined
              prepend-inner-icon="mdi-lock-outline"
              @keydown.space="(event) => event.preventDefault()"
              placeholder="Enter new password"
              color="cyan"
              solo
              flat
              v-model.trim="formData.password"
              @input="validatePassword($event)"
              :rules="[
                validateRules.required(formData.password, 'New Password'),
                validateRules.minLength(formData.password, 'New Password', 8),
                validateRules.maxLength(formData.password, 'New Password', 16),
              ]"
            ></v-text-field>

            <!-- Confirm Password Field -->
            <div
              class="subtitle-2 font-weight-bold grey--text text--darken-2 mb-1 mt-2"
            >
              Confirm Password
            </div>
            <v-text-field
              :append-icon="show3 ? 'mdi-eye' : 'mdi-eye-off'"
              :type="show3 ? 'text' : 'password'"
              v-on:click:append="show3 = !show3"
              dense
              outlined
              solo
              flat
              prepend-inner-icon="mdi-lock-check-outline"
              @keydown.space="(event) => event.preventDefault()"
              placeholder="Confirm new password"
              color="cyan"
              v-model.trim="formData.password_confirmation"
              :rules="[
                validateRules.required(
                  formData.password_confirmation,
                  'Confirm Password'
                ),
                validateRules.confirmPassword(
                  formData.password_confirmation,
                  'Confirm Password',
                  formData.password
                ),
                validateRules.minLength(
                  formData.password_confirmation,
                  'Confirm Password',
                  8
                ),
                validateRules.maxLength(
                  formData.password_confirmation,
                  'Confirm Password',
                  16
                ),
              ]"
            ></v-text-field>

            <!-- Password Requirements Box -->
            <div class="mt-4 pa-4 rounded-lg grey lighten-5 border">
              <div
                class="caption-1 font-weight-bold grey--text text--darken-2 mb-2 text-uppercase"
              >
                <v-icon x-small color="cyan" class="mr-1"
                  >mdi-shield-check-outline</v-icon
                >
                Password Requirements
              </div>
              <div class="d-flex flex-column gap-1">
                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_min ? 'success--text font-weight-medium' : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_min ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_min
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  At least 8 characters long
                </div>
                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_uppercase
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_uppercase ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_uppercase
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Contains an uppercase letter (A-Z)
                </div>
                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_lowercase
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_lowercase ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_lowercase
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Contains a lowercase letter (a-z)
                </div>
                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_number
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_number ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_number
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Contains a number (0-9)
                </div>
                <div
                  class="d-flex align-center font-size-12 my-1"
                  :class="
                    has_special
                      ? 'success--text font-weight-medium'
                      : 'grey--text'
                  "
                >
                  <v-icon
                    x-small
                    :color="has_special ? 'success' : 'grey lighten-1'"
                    class="mr-2"
                  >
                    {{
                      has_special
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  Contains a special character (!@#$%^&*)
                </div>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="mt-5 text-right" v-if="!hideInternalBtn">
              <v-btn
                depressed
                :disabled="!formValid || formLoading"
                :loading="formLoading"
                v-on:click="onSubmit"
                class="custom-bold-button text-capitalize px-6 white--text"
                color="cyan"
              >
                <v-icon left small>mdi-key-change</v-icon>
                Update Password
              </v-btn>
            </div>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ValidationMixin from "@/core/plugins/validation-mixin";
import { PATCH } from "@/core/services/store/request.module";
export default {
  name: "password-update",
  mixins: [ValidationMixin],
  data() {
    return {
      show1: false,
      show2: false,
      show3: false,
      formValid: true,
      has_number: false,
      has_lowercase: false,
      has_uppercase: false,
      has_special: false,
      disabledData: true,
      has_min: false,
      formLoading: false,
      formData: {
        engineer: null,
        password: null,
        password_confirmation: null,
      },
    };
  },
  props: {
    hideInternalBtn: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    onSubmit() {
      const _this = this;
      if (!_this.$refs.passwordForm.validate()) {
        return false;
      }

      _this.formLoading = true;
      _this.$emit("loading", true);
      _this.engineer = _this.$route.params.id;
      let url = "engineer/" + _this.engineer + "/password";
      _this.$store
        .dispatch(PATCH, {
          url,
          data: _this.formData,
        })
        .then(() => {
          _this.formValid = true;
          (_this.formData = {
            password: null,
            password_confirmation: null,
          }),
            (_this.has_number = false);
          _this.has_lowercase = false;
          _this.has_uppercase = false;
          _this.has_special = false;
          _this.has_min = false;
          _this.$emit("password:updated");
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
          _this.$emit("loading", false);
        });
    },
    validatePassword(e) {
      // let result = true;
      let inputVal =
        (this.formData.password ? String(this.formData.password) : "") +
        String(e ? e.key : "");
      let a = /[0-9]+/;
      let b = /[a-z]/;
      let c = /[A-Z]/;
      let d = /[!@#$%^&*)(+=._-]/;
      this.has_number = a.test(inputVal);
      this.has_lowercase = b.test(inputVal);
      this.has_uppercase = c.test(inputVal);
      if (e && e == e.toUpperCase()) {
        this.has_lowercase = false;
      }
      this.has_special = d.test(inputVal);
      if (this.formData.password && this.formData.password.length > 7) {
        this.has_min = true;
      } else {
        this.has_min = false;
      }

      if (
        this.has_number &&
        this.has_lowercase &&
        this.has_uppercase &&
        this.has_special &&
        this.has_min &&
        this.has_max
      ) {
        console.log("result");
        this.formValid = true;
        /*  this.disabledData = false; */
      } else {
        /*  this.disabledData = true; */
        this.formValid = false;
      }
    },
    // manageLimit(e) {
    //   // this.disabledDate();
    //   if (this.formData.password && this.formData.password.length > 15) {
    //     e.preventDefault();
    //   }
    // },
    // manageLimitCon(e) {
    //   if (this.formData.password && this.formData.password.length > 15) {
    //     e.preventDefault();
    //   }
    // },
    mounted() {
      const _this = this;
      _this.formData.engineer = this.$route.params.id;
    },
    created() {
      const _this = this;
      _this.engineer = _this.$route.params.id;
    },
  },
};
</script>

<style scoped>
.frmValidation {
  font-size: 13px;
  margin-bottom: 6px !important;
}
.frmValidation--passed {
  color: #717b85;
}
.frmIcon {
  color: #eb0029;
}
.frmValidation--passed .frmIcon {
  color: #0fa140;
}

.howToBuild {
  text-align: center;
  color: purple;
}
.howToBuild a {
  color: grey;
  font-weight: bold;
  text-decoration: none;
  text-transform: uppercase;
}
</style>
